import { Link } from "@tanstack/react-router";
import { useState, useEffect, useRef, useCallback } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import blancoLogo from "@/assets/blanco-logo-xs.webp.asset.json";

const usageMenu = {
  overview: { to: "/usage", label: "Usage Overview" },
  categories: [
    { slug: "residential", label: "Residential", blog: "residential-epoxy-flooring-nj" },
    { slug: "commercial", label: "Commercial", blog: "commercial-epoxy-flooring-nj" },
    { slug: "industrial", label: "Industrial", blog: "industrial-epoxy-flooring-nj" },
  ],
};

type MenuKey = "services" | "usage" | null;

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState<MenuKey>(null);
  const [progress, setProgress] = useState(0);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openMenu = useCallback((key: Exclude<MenuKey, null>) => {
    clearTimer();
    setMenu(key);
  }, []);

  const scheduleClose = useCallback(() => {
    clearTimer();
    closeTimer.current = setTimeout(() => setMenu(null), 140);
  }, []);

  useEffect(() => () => clearTimer(), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        clearTimer();
        setMenu(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? Math.min(1, y / h) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const servicesOpen = menu === "services";
  const usageOpen = menu === "usage";
  const closeAll = () => {
    clearTimer();
    setMenu(null);
  };

  const linkBase =
    "font-display text-[13px] font-semibold text-foreground/80 hover:text-gold transition-colors";


  return (
    <header className="fixed top-0 left-0 right-0 z-50 px-3 sm:px-4 lg:px-6 pt-4">
      <nav
        className={`relative mx-auto max-w-6xl flex items-center justify-between pl-3 pr-1.5 py-2 rounded-full border border-gold/25 bg-background/65 backdrop-blur-xl transition-all duration-500 ${
          scrolled
            ? "shadow-[0_18px_50px_-12px_rgba(0,0,0,0.18)]"
            : "shadow-[0_12px_40px_-16px_rgba(0,0,0,0.15)]"
        } hover:shadow-[0_20px_60px_-12px_rgba(201,168,76,0.22)]`}
      >
        {/* Brand */}
        <Link
          to="/"
          className="flex items-center gap-2.5 pl-1 group shrink-0"
          onClick={() => setOpen(false)}
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8a6a1f] via-[#b8902f] to-[#5c4416] border border-gold/50 flex items-center justify-center shadow-[0_6px_18px_-4px_rgba(138,106,31,0.55)] group-hover:scale-110 transition-transform">
            <img
              src={blancoLogo.url}
              alt="Blanco Pro Services"
              width={28}
              height={28}
              decoding="async"
              fetchPriority="high"
              className="w-7 h-7 object-contain drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]"
            />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display text-[15px] font-bold tracking-tight">
              Blanco Pro
            </span>
            <span className="text-[9px] uppercase tracking-[0.22em] text-gold font-semibold mt-0.5">
              Services
            </span>
          </div>
        </Link>

        {/* Center links */}
        <ul className="hidden lg:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
          <li>
            <Link
              to="/"
              className={linkBase}
              activeProps={{ className: "text-gold" }}
              activeOptions={{ exact: true }}
            >
              Home
            </Link>
          </li>

          {/* Services dropdown */}
          <li
            className="group relative"
            onMouseEnter={() => openMenu("services")}
            onMouseLeave={scheduleClose}
            onFocus={() => openMenu("services")}
          >
            <button
              className={`${linkBase} flex items-center gap-1.5`}
              onClick={() => (servicesOpen ? closeAll() : openMenu("services"))}
              aria-expanded={servicesOpen}
              aria-haspopup="true"
            >
              Services
              <ChevronDown
                className={`w-3.5 h-3.5 opacity-60 transition-transform ${
                  servicesOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 ${
                servicesOpen ? "" : "pointer-events-none"
              }`}
            >
              <div
                data-open={servicesOpen}
                className="dropdown-panel w-64 rounded-2xl border border-border bg-background/95 backdrop-blur-xl shadow-elegant overflow-hidden"
              >
                <Link
                  to="/services"
                  onClick={closeAll}
                  className="block px-5 py-3 text-sm hover:bg-muted transition-colors border-b border-border"
                >
                  <div className="font-display font-semibold">All Services</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Epoxy, hardscaping & demo
                  </div>
                </Link>
                <Link
                  to="/color-chart"
                  onClick={closeAll}
                  className="block px-5 py-3 text-sm hover:bg-muted transition-colors"
                >
                  <div className="font-display font-semibold inline-flex items-center gap-2">
                    <span className="text-gold">✦</span> Color Chart
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Browse flake blends & palettes
                  </div>
                </Link>
              </div>
            </div>

          </li>

          {/* Usage dropdown */}
          <li
            className="group relative"
            onMouseEnter={() => openMenu("usage")}
            onMouseLeave={scheduleClose}
            onFocus={() => openMenu("usage")}
          >
            <button
              className={`${linkBase} flex items-center gap-1.5`}
              onClick={() => (usageOpen ? closeAll() : openMenu("usage"))}
              aria-expanded={usageOpen}
              aria-haspopup="true"
            >
              Usage
              <ChevronDown
                className={`w-3.5 h-3.5 opacity-60 transition-transform ${
                  usageOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 ${
                usageOpen ? "" : "pointer-events-none"
              }`}
            >
              <div
                data-open={usageOpen}
                className="dropdown-panel w-72 rounded-2xl border border-border bg-background/95 backdrop-blur-xl shadow-elegant overflow-hidden"
              >
                <Link
                  to={usageMenu.overview.to}
                  onClick={closeAll}
                  className="block px-5 py-3 text-sm font-medium hover:bg-muted transition-colors border-b border-border"
                >
                  <div className="font-display font-semibold">
                    {usageMenu.overview.label}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    All applications at a glance
                  </div>
                </Link>
                <div className="py-1">
                  {usageMenu.categories.map((c) => (
                    <div key={c.slug} className="px-2">
                      <div className="grid grid-cols-2 gap-1 p-1">
                        <Link
                          to="/usage/$category"
                          params={{ category: c.slug }}
                          onClick={closeAll}
                          className="px-3 py-2 rounded-lg text-xs hover:bg-muted transition-colors"
                        >
                          <div className="font-semibold">{c.label}</div>
                          <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                            Applications
                          </div>
                        </Link>
                        <Link
                          to="/blog/$slug"
                          params={{ slug: c.blog }}
                          onClick={closeAll}
                          className="px-3 py-2 rounded-lg text-xs hover:bg-muted transition-colors"
                        >
                          <div className="font-semibold inline-flex items-center gap-1">
                            <span className="text-gold">✦</span> Guide
                          </div>
                          <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                            Read article
                          </div>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
                <Link
                  to="/blog"
                  onClick={closeAll}
                  className="block px-5 py-3 text-xs uppercase tracking-[0.2em] text-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors border-t border-border"
                >
                  All Guides →
                </Link>
              </div>
            </div>
          </li>

          <li>
            <Link to="/service-area" className={linkBase} activeProps={{ className: "text-gold" }}>
              Service Area
            </Link>
          </li>
          <li>
            <Link to="/about" className={linkBase} activeProps={{ className: "text-gold" }}>
              About
            </Link>
          </li>
          <li>
            <Link to="/contact" className={linkBase} activeProps={{ className: "text-gold" }}>
              Contact
            </Link>
          </li>
        </ul>

        {/* CTA */}
        <a
          href="tel:9088956133"
          className="hidden md:flex items-center gap-3 pl-5 pr-1.5 py-1.5 bg-foreground text-background rounded-full hover:bg-foreground/90 transition-all group shrink-0"
        >
          <span className="text-[10px] font-bold tracking-[0.18em] uppercase opacity-80 group-hover:opacity-100">
            Call Now
          </span>
          <span className="flex items-center bg-gradient-gold text-gold-foreground rounded-full px-3.5 py-1.5 font-bold text-[13px] shadow-gold">
            908-895-6133
          </span>
        </a>

        {/* Mobile toggle */}
        <button
          className="lg:hidden p-2 mr-1"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Bottom gold glow line */}
        <div className="pointer-events-none absolute bottom-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

        {/* Scroll progress — origin-left scaleX, GPU only */}
        <div className="pointer-events-none absolute -bottom-px left-4 right-4 h-[2px] overflow-hidden rounded-full">
          <div
            className="h-full bg-gradient-gold origin-left"
            style={{ transform: `scaleX(${progress})`, transition: "transform 120ms linear" }}
          />
        </div>
      </nav>


      {/* Mobile menu panel */}
      {open && (
        <div className="lg:hidden mx-auto max-w-6xl mt-3 rounded-3xl border border-gold/25 bg-background/95 backdrop-blur-xl shadow-elegant animate-fade-in max-h-[80vh] overflow-y-auto">
          <nav className="flex flex-col px-4 py-4 gap-1">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="px-4 py-3 rounded-xl text-base font-medium hover:bg-muted transition-colors"
            >
              Home
            </Link>
            <Link
              to="/services"
              onClick={() => setOpen(false)}
              className="px-4 py-3 rounded-xl text-base font-medium hover:bg-muted transition-colors"
            >
              Services
            </Link>
            <Link
              to="/color-chart"
              onClick={() => setOpen(false)}
              className="px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-muted transition-colors"
            >
              <span className="text-gold mr-1">✦</span> Color Chart
            </Link>

            <div className="mt-2 px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.25em] text-gold">
              Usage
            </div>
            <Link
              to="/usage"
              onClick={() => setOpen(false)}
              className="px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-muted transition-colors"
            >
              Usage Overview
            </Link>
            {usageMenu.categories.map((c) => (
              <div key={c.slug} className="grid grid-cols-2 gap-1">
                <Link
                  to="/usage/$category"
                  params={{ category: c.slug }}
                  onClick={() => setOpen(false)}
                  className="px-4 py-2.5 rounded-xl text-sm hover:bg-muted transition-colors"
                >
                  {c.label}
                </Link>
                <Link
                  to="/blog/$slug"
                  params={{ slug: c.blog }}
                  onClick={() => setOpen(false)}
                  className="px-4 py-2.5 rounded-xl text-sm hover:bg-muted transition-colors"
                >
                  <span className="text-gold mr-1">✦</span> Guide
                </Link>
              </div>
            ))}
            <Link
              to="/blog"
              onClick={() => setOpen(false)}
              className="px-4 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-muted transition-colors"
            >
              All Guides
            </Link>

            <div className="mt-2 border-t border-border pt-2">
              {[
                { to: "/service-area", label: "Service Area" },
                { to: "/about", label: "About" },
                { to: "/contact", label: "Contact" },
              ].map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3 rounded-xl text-base font-medium hover:bg-muted transition-colors"
                >
                  {n.label}
                </Link>
              ))}
            </div>

            <a
              href="tel:9088956133"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-foreground text-background px-5 py-3 rounded-full text-sm font-semibold"
            >
              <span className="bg-gradient-gold text-gold-foreground rounded-full px-3 py-1 text-xs">
                908-895-6133
              </span>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
